import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { findActiveMode } from '../contexts/inventoryModes';

/**
 * useInventoryMode — activates an Inventory Mode based on URL search
 * params. Returns { mode, context } where mode is the registered mode
 * definition and context is the resolved (mode-specific) data object.
 *
 * Both are null when no mode is active. The hook is safe to call on
 * inventory routes that aren't engaged in any mode — it returns nulls
 * and does nothing.
 *
 * Stale-resolve guard: if the URL changes mid-resolve, the in-flight
 * resolution is ignored. The mode flips to null immediately on URL
 * change; the new context lands when its resolve finishes.
 */
export default function useInventoryMode({ caseId }) {
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState(null);
  const [context, setContext] = useState(null);

  useEffect(() => {
    const active = findActiveMode(searchParams);
    if (!active) {
      setMode(null);
      setContext(null);
      return undefined;
    }

    setMode(active);
    setContext(null);

    let cancelled = false;
    Promise.resolve(
      active.resolveContext({
        caseId,
        params: searchParams,
        sessionStorage: typeof window !== 'undefined' ? window.sessionStorage : null,
      }),
    )
      .then((resolved) => {
        if (cancelled) return;
        if (!resolved) {
          // Resolve failed; treat as no-op (banner won't render without context).
          setMode(null);
          setContext(null);
          return;
        }
        setContext(resolved);
      })
      .catch(() => {
        if (cancelled) return;
        setMode(null);
        setContext(null);
      });

    return () => {
      cancelled = true;
    };
  }, [searchParams, caseId]);

  return { mode, context };
}
